import moment from 'moment';
import type { ConcludedCardDetail } from './TaskBoardCardViewModal';
import { COLUMN_ACCENT_COLORS } from './task-board.constants';

export type ConcludedGroup = {
  sourceListId: string | null;
  sourceListName: string;
  cards: ConcludedCardDetail[];
};

type Props = {
  groups: ConcludedGroup[];
  onCardClick: (card: ConcludedCardDetail) => void;
};

function computeDueBadge(dueDate?: string | null) {
  if (!dueDate) return null;
  const m = moment(String(dueDate).slice(0, 10), 'YYYY-MM-DD');
  if (!m.isValid()) return null;
  return { label: m.format('MM/DD/YYYY'), variant: 'neutral' };
}

const ConcludedBoard = ({ groups, onCardClick }: Props) => (
  <div className="task-board__scroll">
    {groups.map((group, groupIndex) => {
      const accentColor = COLUMN_ACCENT_COLORS[groupIndex % COLUMN_ACCENT_COLORS.length];
      return (
        <div
          key={`${group.sourceListId ?? 'none'}-${group.sourceListName}-${groupIndex}`}
          className="task-board__column"
        >
          <div className="task-board__column-accent" style={{ backgroundColor: accentColor }} />
          <div className="task-board__column-header">
            <span className="task-board__column-title">{group.sourceListName}</span>
            <span className="task-board__column-count">{group.cards.length}</span>
          </div>
          <div className="task-board__column-body">
            {group.cards.length === 0 ? (
              <div className="task-board__empty-state task-board__empty-state--compact">No cards.</div>
            ) : (
              group.cards.map((card) => {
                const desc = (card.description || '').trim();
                const preview = desc.length > 96 ? `${desc.slice(0, 96).trimEnd()}...` : desc;
                const dueBadge = computeDueBadge(card.dueDate);
                const checklistTotals = (card.checklists || []).reduce(
                  (acc, cl) => {
                    acc.total += cl.items.length;
                    acc.done += cl.items.filter((it) => it.isDone).length;
                    return acc;
                  },
                  { total: 0, done: 0 },
                );

                return (
                  <div
                    key={card.id}
                    className="task-board__card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick(card)}
                  >
                    <div className="task-board__card-top">
                      <div className="task-board__card-labels-row">
                        {(card.simpleLabels || []).slice(0, 3).map((label) => (
                          <span
                            key={label}
                            className="task-board__card-label task-board__card-label--simple"
                            title={label}
                          >
                            {label}
                          </span>
                        ))}
                        {(card.simpleLabels || []).length > 3 && (
                          <span className="task-board__card-label task-board__card-label--more">
                            +{(card.simpleLabels || []).length - 3}
                          </span>
                        )}
                      </div>
                      {(card.labelsData || []).length > 0 && (
                        <div className="task-board__card-labels-row">
                          {(card.labelsData || []).slice(0, 4).map((label, idx) => (
                            <span
                              key={idx}
                              className="task-board__card-label"
                              style={{ backgroundColor: label.color || '#6b7280', color: '#fff' }}
                              title={label.text}
                            >
                              {label.text}
                            </span>
                          ))}
                          {(card.labelsData || []).length > 4 && (
                            <span className="task-board__card-label task-board__card-label--more">
                              +{(card.labelsData || []).length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="task-board__card-title" style={{ textDecoration: 'line-through' }}>
                      {card.title}
                    </div>
                    {!!preview && <div className="task-board__card-desc">{preview}</div>}
                    <div className="task-board__card-meta">
                      {dueBadge && (
                        <span className={`task-board__card-pill task-board__card-pill--${dueBadge.variant}`}>
                          <i className="far fa-calendar-alt" />
                          {dueBadge.label}
                        </span>
                      )}
                      {checklistTotals.total > 0 && (
                        <span className="task-board__card-pill task-board__card-pill--info">
                          <i className="fas fa-check-square" />
                          {checklistTotals.done}/{checklistTotals.total}
                        </span>
                      )}
                      {!!(card.members || []).length && (
                        <span className="task-board__card-pill task-board__card-pill--neutral">
                          <i className="fas fa-user" />
                          {(card.members || []).length}
                        </span>
                      )}
                      {!!(card.attachments || []).length && (
                        <span className="task-board__card-pill task-board__card-pill--neutral">
                          <i className="fas fa-paperclip" />
                          {(card.attachments || []).length}
                        </span>
                      )}
                      {!!card.commentsCount && (
                        <span className="task-board__card-pill task-board__card-pill--neutral">
                          <i className="far fa-comment-alt" />
                          {card.commentsCount}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      );
    })}
  </div>
);

export default ConcludedBoard;
